// src/utils/sound.ts

// =====================================================
// AUDIO FILES
// =====================================================

// Background
const bgMusic =
  new Audio("/sounds/bg.mp3");

// Existing game sounds
const winSound =
  new Audio("/sounds/win.mp3");

const loseSound =
  new Audio("/sounds/lose.mp3");

const cardSound =
  new Audio("/sounds/card.mp3");

const coolSound =
  new Audio("/sounds/cool.mp3");


// =====================================================
// CINEMATIC / EVENT SOUNDS
// =====================================================

const roundSound =
  new Audio("/sounds/round.mp3");

const soorSound =
  new Audio("/sounds/soor.mp3");

const soorCancelledSound =
  new Audio("/sounds/soor-cancelled.mp3");

const clubBonusSound =
  new Audio("/sounds/club-17.mp3");

const sweetSound =
  new Audio("/sounds/sweet.mp3");


// =====================================================
// ALL AUDIO
// =====================================================

const allAudio: HTMLAudioElement[] = [
  bgMusic,
  winSound,
  loseSound,
  cardSound,
  coolSound,
  roundSound,
  soorSound,
  soorCancelledSound,
  clubBonusSound,
  sweetSound,
];


// =====================================================
// SETTINGS
// =====================================================

bgMusic.loop = true;


// Background should stay underneath game effects.
bgMusic.volume = 0.4;


// Card placed on the table.
cardSound.volume = 0.7;

// Successful Jack collection reaction.
coolSound.volume = 0.9;


// Game-result sounds.
winSound.volume = 0.85;
loseSound.volume = 0.8;


// Cinematic sounds.
roundSound.volume = 0.8;
soorSound.volume = 0.9;
soorCancelledSound.volume = 0.85;
clubBonusSound.volume = 0.95;
sweetSound.volume = 0.95;


// =====================================================
// PRELOAD
// =====================================================

for (
  const audio of allAudio
) {

  audio.preload =
    "auto";

}


// =====================================================
// DEBUG
// =====================================================

bgMusic.onerror = () =>
  console.error(
    "❌ BG MUSIC FAILED"
  );

winSound.onerror = () =>
  console.error(
    "❌ WIN SOUND FAILED"
  );

loseSound.onerror = () =>
  console.error(
    "❌ LOSE SOUND FAILED"
  );

cardSound.onerror = () =>
  console.error(
    "❌ CARD SOUND FAILED"
  );

coolSound.onerror = () =>
  console.error(
    "❌ COOL SOUND FAILED"
  );

roundSound.onerror = () =>
  console.error(
    "❌ ROUND SOUND FAILED"
  );

soorSound.onerror = () =>
  console.error(
    "❌ SOOR SOUND FAILED"
  );

soorCancelledSound.onerror = () =>
  console.error(
    "❌ SOOR CANCELLED SOUND FAILED"
  );

clubBonusSound.onerror = () =>
  console.error(
    "❌ CLUB 17 BONUS SOUND FAILED"
  );

sweetSound.onerror = () =>
  console.error(
    "❌ SWEET SOUND FAILED"
  );


// =====================================================
// AUDIO STATE
// =====================================================

/*
  GameBoard controls whether background music
  should currently be active.

  This prevents focus/visibility recovery from
  restarting background music after the player
  leaves the game.
*/

let backgroundRequested =
  false;


let interactionListenersInstalled =
  false;


// =====================================================
// SAFE PLAY
// =====================================================

const safePlay = (
  audio: HTMLAudioElement,
  label: string
) => {

  const playPromise =
    audio.play();

  if (
    playPromise !== undefined
  ) {

    playPromise.catch(
      (err) => {

        console.warn(
          `⚠️ ${label} playback blocked:`,
          err
        );

      }
    );

  }

};


// =====================================================
// PLAY EFFECT
// =====================================================

const playSound = (
  audio: HTMLAudioElement
) => {

  /*
    Restart the sound from the beginning.

    This preserves the existing P-ARENA behavior
    when the same event occurs again before the
    previous sound has completely finished.
  */

  audio.pause();

  try {

    audio.currentTime =
      0;

  } catch (err) {

    console.warn(
      "⚠️ Could not reset sound:",
      err
    );

  }


  safePlay(
    audio,
    "Game sound"
  );

};


// =====================================================
// BACKGROUND PLAYBACK
// =====================================================

const tryPlayBackground =
  () => {

    if (
      !backgroundRequested
    ) {
      return;
    }


    /*
      Do not start background music while
      the browser tab is hidden.
    */

    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      return;
    }


    if (
      bgMusic.paused
    ) {

      safePlay(
        bgMusic,
        "Background music"
      );

    }

  };


// =====================================================
// USER-INTERACTION RECOVERY
// =====================================================

const handleUserInteraction =
  () => {

    /*
      If autoplay was rejected when GameBoard mounted,
      retry after a real user interaction.
    */

    tryPlayBackground();

  };


const installInteractionRecovery =
  () => {

    if (
      interactionListenersInstalled ||
      typeof window === "undefined"
    ) {
      return;
    }


    interactionListenersInstalled =
      true;


    window.addEventListener(
      "pointerdown",
      handleUserInteraction,
      {
        passive: true,
      }
    );


    window.addEventListener(
      "touchstart",
      handleUserInteraction,
      {
        passive: true,
      }
    );


    window.addEventListener(
      "keydown",
      handleUserInteraction
    );

  };


// =====================================================
// TAB / APP RECOVERY
// =====================================================

const handleVisibilityChange =
  () => {

    if (
      typeof document === "undefined"
    ) {
      return;
    }


    if (
      document.visibilityState === "visible"
    ) {

      /*
        Mobile browsers may suspend media while
        the tab/app is in the background.

        Retry when P-ARENA becomes visible again.
      */

      tryPlayBackground();

    }

  };


const handleWindowFocus =
  () => {

    tryPlayBackground();

  };


// =====================================================
// INSTALL GLOBAL RECOVERY
// =====================================================

if (
  typeof window !== "undefined"
) {

  installInteractionRecovery();


  window.addEventListener(
    "focus",
    handleWindowFocus
  );

}


if (
  typeof document !== "undefined"
) {

  document.addEventListener(
    "visibilitychange",
    handleVisibilityChange
  );

}


// =====================================================
// SOUND CONTROLLER
// =====================================================

export const Sound = {

  // ===================================================
  // BACKGROUND
  // ===================================================

  playBackground() {

    backgroundRequested =
      true;


    tryPlayBackground();

  },


  stopBackground() {

    /*
      Mark background as intentionally stopped first.

      This prevents focus/visibility recovery from
      restarting music after leaving GameBoard.
    */

    backgroundRequested =
      false;


    bgMusic.pause();


    try {

      bgMusic.currentTime =
        0;

    } catch (err) {

      console.warn(
        "⚠️ Could not reset background music:",
        err
      );

    }

  },


  // ===================================================
  // NORMAL GAME SOUNDS
  // ===================================================

  playCard() {

    playSound(
      cardSound
    );

  },


  // ===================================================
  // SUCCESSFUL JACK COLLECTION
  // ===================================================

  playCool() {

    playSound(
      coolSound
    );

  },


  // ===================================================
  // ROUND CINEMATIC
  // ===================================================

  playRound() {

    playSound(
      roundSound
    );

  },


  // ===================================================
  // SOOR
  // ===================================================

  playSoor() {

    playSound(
      soorSound
    );

  },


  // ===================================================
  // SOOR CANCELLATION
  // ===================================================

  playSoorCancelled() {

    playSound(
      soorCancelledSound
    );

  },


  // ===================================================
  // 17 POINT CLUB BONUS
  // ===================================================

  playClubBonus() {

    playSound(
      clubBonusSound
    );

  },


  // ===================================================
  // ACE + 10♦ = SWEET
  // ===================================================

  playSweet() {

    playSound(
      sweetSound
    );

  },


  // ===================================================
  // GAME RESULT
  // ===================================================

  playWin() {

    playSound(
      winSound
    );

  },


  playLose() {

    playSound(
      loseSound
    );

  },

};
